// Vocabulario y helpers puros de Administración · Resumen (T-cdqn). Sin
// JSX ni hooks: la pantalla los compone y los tests los prueban solos.
//
// La puesta a punto llega casi lista del servidor (clave, `ok`, resumen
// en es-CL); aquí se le pone a cada clave su título, su destino y el
// orden del handoff, y se arma en el cliente la única fila que core-api
// no puede calcular: «Equipo invitado», a partir del roster y las
// invitaciones pendientes de auth-svc.
// Las rutas salen del registro de secciones (lib/nav.ts), nunca de un
// string suelto, así la fila y el menú no pueden discrepar.

import type { ConfigAuditItem, OrgMember, SetupStatusItem } from "@bitacorapro/api-types";
import type { AdminWords } from "../../../lib/admin/words";
import { ADMIN_SECTION_NAV, adminSectionPath } from "../../../lib/nav";

export type FilaPuestaAPunto = {
  key: SetupStatusItem["key"] | "equipo";
  titulo: string;
  summary: string;
  ok: boolean;
  /** Ruta de la sección que resuelve la fila. */
  to: string;
  /** El `title` del botón: qué pasa al hacer clic. */
  title: string;
};

/** Miembros que hoy pueden entrar: `disabledAt` nulo. Un roster viejo sin
 *  la clave en el cable cuenta como activo. */
export function miembrosActivos(members: ReadonlyArray<Pick<OrgMember, "disabledAt">>): number {
  return members.filter((m) => m.disabledAt === null || m.disabledAt === undefined).length;
}

// «Equipo invitado»: en orden mientras hay al menos otra persona además
// de quien administra, o ya se invitó a alguien que todavía no aceptó.
// El resumen lleva los dos conteos siempre, como el prototipo
// («n personas · m invitaciones pendientes»).
export function filaEquipo(
  members: ReadonlyArray<Pick<OrgMember, "disabledAt">>,
  pendientes: number
): {
  ok: boolean;
  summary: string;
} {
  const n = miembrosActivos(members);
  const integrantes = n === 1 ? "1 integrante" : `${n} integrantes`;
  const invitaciones =
    pendientes === 1 ? "1 invitación pendiente" : `${pendientes} invitaciones pendientes`;
  return { ok: n >= 2 || pendientes >= 1, summary: `${integrantes} · ${invitaciones}` };
}

// Checklists: su sección de Administración todavía no tiene pantalla
// (ADMIN_SECTION_NAV la registra `available: false`); mientras tanto el
// panel de plantillas vive en Configuración. Cuando la sección se
// construya, cambia sola la bandera y esta fila la sigue.
export function rutaChecklists(): string {
  const seccion = ADMIN_SECTION_NAV.find((s) => s.section === "checklists");
  return seccion?.available ? seccion.path : "/configuracion";
}

const ORDEN: ReadonlyArray<FilaPuestaAPunto["key"]> = [
  "datos_logo",
  "equipo",
  "activos",
  "checklists",
];

/**
 * Las filas del panel «Puesta a punto», en el orden del handoff: Datos y
 * logo · Equipo invitado · {Tiendas y zonas} · Checklists asignados. Una
 * clave que el servidor no mande se omite (el contrato lo permite); la
 * de equipo se calcula aquí con el roster y las invitaciones pendientes.
 */
export function filasPuestaAPunto(args: {
  items: ReadonlyArray<SetupStatusItem>;
  members: ReadonlyArray<Pick<OrgMember, "disabledAt">>;
  invitacionesPendientes: number;
  words: AdminWords;
}): FilaPuestaAPunto[] {
  const { items, members, invitacionesPendientes, words } = args;
  const porClave = new Map(items.map((i) => [i.key, i]));
  const filas: FilaPuestaAPunto[] = [];
  for (const key of ORDEN) {
    if (key === "equipo") {
      const equipo = filaEquipo(members, invitacionesPendientes);
      filas.push({
        key,
        titulo: "Equipo invitado",
        summary: equipo.summary,
        ok: equipo.ok,
        to: adminSectionPath("equipo"),
        title: `Abre ${words.sections.equipo} para invitar o revisar a tu equipo`,
      });
      continue;
    }
    const item = porClave.get(key);
    if (!item) continue;
    switch (key) {
      case "datos_logo":
        filas.push({
          key,
          titulo: "Datos y logo",
          summary: item.summary,
          ok: item.ok,
          to: adminSectionPath("empresa"),
          title: `Abre ${words.sections.empresa} para revisar razón social, RUT y logo`,
        });
        break;
      case "activos":
        filas.push({
          key,
          titulo: words.sections.activos,
          summary: item.summary,
          ok: item.ok,
          to: adminSectionPath("activos"),
          title: `Abre ${words.sections.activos} para revisar lo que falta`,
        });
        break;
      case "checklists":
        filas.push({
          key,
          titulo: "Checklists asignados",
          summary: item.summary,
          ok: item.ok,
          to: rutaChecklists(),
          title: "Abre los checklists para asignar los que faltan",
        });
        break;
    }
  }
  return filas;
}

const DAY_MS = 86_400_000;

// Hora en Chile, como el registro completo y el CSV del servidor: el
// gerente lee el Resumen desde donde esté. Armado por partes: es-CL
// separa día y mes con guión («01-oct») y pone «a m.» si se le deja la
// hora de 12.
const HORA = new Intl.DateTimeFormat("es-CL", {
  timeZone: "America/Santiago",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const FECHA = new Intl.DateTimeFormat("es-CL", {
  timeZone: "America/Santiago",
  day: "2-digit",
  month: "short",
});
// Fecha civil en Chile, para contar días de calendario y no bloques de
// 24 h: algo de ayer a las 20:00 es «ayer» aunque hayan pasado 15 h.
const DIA = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Santiago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function diaCivil(d: Date): number {
  const [y, m, dd] = DIA.format(d).split("-").map(Number);
  return Date.UTC(y!, m! - 1, dd);
}

/**
 * La fecha de un ítem del timeline, corta y relativa como en el prototipo:
 * «hoy 11:02», «ayer 17:40», y «27 oct» desde los dos días hacia atrás.
 * `now` es parámetro para que la función sea pura y los tests no
 * congelen el reloj.
 */
export function fmtCuandoCorto(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dias = Math.round((diaCivil(now) - diaCivil(d)) / DAY_MS);
  if (dias === 0) return `hoy ${HORA.format(d)}`;
  if (dias === 1) return `ayer ${HORA.format(d)}`;
  const parts = new Map(FECHA.formatToParts(d).map((p) => [p.type, p.value]));
  return `${parts.get("day")} ${(parts.get("month") ?? "").replace(".", "")}`;
}

/** Quién hizo un cambio hecho por alguien de la organización: el nombre
 *  del equipo si el correo está en el roster, si no el correo tal cual;
 *  «Sistema» cuando el evento no trae a nadie. Para soporte la pantalla
 *  pinta la pill «BitácoraPro» y no llama aquí. */
export function quienLabel(
  actor: ConfigAuditItem["actor"],
  rosterByEmail: ReadonlyMap<string, Pick<OrgMember, "displayName">>
): string {
  if (!actor.name) return "Sistema";
  return rosterByEmail.get(actor.name.toLowerCase())?.displayName ?? actor.name;
}

export function rosterPorCorreo(
  members: ReadonlyArray<Pick<OrgMember, "email" | "displayName">> | undefined
): ReadonlyMap<string, Pick<OrgMember, "displayName">> {
  const map = new Map<string, Pick<OrgMember, "displayName">>();
  for (const m of members ?? []) map.set(m.email.toLowerCase(), m);
  return map;
}
